import { useState, useEffect } from 'react';
import { getAllClients } from '../services/api';
import { Skeleton } from '../components/ui/skeleton';
import { Search, Users, Building2, MapPin, FileDown, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  marketalert: { label: 'MarketAlert 19€', color: 'bg-[#397DFF]/10 text-[#397DFF]' },
  marketalert_no_engagement: { label: 'MarketAlert 29€', color: 'bg-[#397DFF]/10 text-[#397DFF]' },
  serenite: { label: 'Sérénité', color: 'bg-[#10D183]/10 text-[#10D183]' },
  pro: { label: 'Pro', color: 'bg-[#10D183]/10 text-[#10D183]' },
  starter: { label: 'Starter', color: 'bg-[#10D183]/10 text-[#10D183]' },
  elite: { label: 'Elite', color: 'bg-[#10D183]/10 text-[#10D183]' },
};

const AllClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterFranchise, setFilterFranchise] = useState('all');

  const fetchClients = () => {
    setLoading(true);
    getAllClients()
      .then(res => setClients(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Supprimer ${client.name} (${client.email}) ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/superadmin/clients/${client.id}`);
      toast.success(`${client.name} supprimé`);
      setClients(prev => prev.filter(c => c.id !== client.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const franchises = [...new Set(clients.map(c => c.franchise_name))].sort();

  const filtered = clients.filter(c => {
    const matchSearch = !searchTerm || 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || c.client_type === filterType;
    const matchFranchise = filterFranchise === 'all' || c.franchise_name === filterFranchise;
    return matchSearch && matchType && matchFranchise;
  });

  // Stats
  const count19 = clients.filter(c => !c.client_type || c.client_type === 'marketalert').length;
  const count29 = clients.filter(c => c.client_type === 'marketalert_no_engagement').length;
  const countAccomp = clients.filter(c => ['serenite', 'pro', 'starter', 'elite'].includes(c.client_type)).length;

  const handleExportCSV = () => {
    const rows = [['Client', 'Email', 'Entreprise', 'Franchisé', 'Agence', 'Formule', 'Date création']];
    filtered.forEach(c => {
      const t = TYPE_LABELS[c.client_type] || TYPE_LABELS['marketalert'];
      rows.push([c.name, c.email, c.company || '', c.franchise_name, c.agency_name || '', t.label, c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '']);
    });
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 bg-white" />
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 bg-white" />)}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="all-clients-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E]">Tous les clients</h1>
          <p className="text-sm text-[#687175] mt-1">{clients.length} client(s) au total</p>
        </div>
        <Button onClick={handleExportCSV} className="btn-primary" data-testid="export-clients-csv">
          <FileDown className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Stats facturation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-4 text-center">
          <p className="text-2xl font-bold text-[#397DFF]">{count19}</p>
          <p className="text-xs text-[#687175] mt-1">MarketAlert 19€</p>
        </div>
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-4 text-center">
          <p className="text-2xl font-bold text-[#397DFF]">{count29}</p>
          <p className="text-xs text-[#687175] mt-1">MarketAlert 29€</p>
        </div>
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-4 text-center">
          <p className="text-2xl font-bold text-[#10D183]">{countAccomp}</p>
          <p className="text-xs text-[#687175] mt-1">Accompagnés</p>
        </div>
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-4 text-center">
          <p className="text-2xl font-bold text-[#191D1E]">{count19 * 19 + count29 * 29}€</p>
          <p className="text-xs text-[#687175] mt-1">Facturation/mois</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#939BA0]" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-[#BFC6CC] text-[#191D1E]" placeholder="Rechercher un client..."
            data-testid="search-clients" />
        </div>
        <select value={filterFranchise} onChange={(e) => setFilterFranchise(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white border border-[#BFC6CC] text-[#191D1E] text-sm"
          data-testid="filter-franchise">
          <option value="all">Tous les franchisés</option>
          {franchises.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white border border-[#BFC6CC] text-[#191D1E] text-sm"
          data-testid="filter-type">
          <option value="all">Toutes les formules</option>
          <option value="marketalert">MarketAlert 19€</option>
          <option value="marketalert_no_engagement">MarketAlert 29€</option>
          <option value="serenite">Sérénité</option>
          <option value="pro">Pro</option>
          <option value="starter">Starter</option>
          <option value="elite">Elite</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-semibold text-[#687175] uppercase tracking-wider border-b border-[#BFC6CC]/10">
                <th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Franchisé</th>
                <th className="px-5 py-3">Agence</th>
                <th className="px-5 py-3">Formule</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]/10">
              {filtered.map((c, i) => {
                const typeInfo = TYPE_LABELS[c.client_type] || TYPE_LABELS['marketalert'];
                return (
                  <motion.tr key={c.id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }} className="hover:bg-[#F5F6F9]"
                    data-testid={`client-row-${c.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#397DFF]/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-[#397DFF]">
                            {c.name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#191D1E] text-sm">{c.name}</p>
                          <p className="text-xs text-[#687175]">{c.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-[#687175]" />
                        <span className="text-sm text-[#191D1E]">{c.franchise_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {c.agency_name !== '—' ? (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-[#687175]" />
                          <span className="text-sm text-[#687175]">{c.agency_name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-[#939BA0]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-[#687175]">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => handleDelete(c)}
                        className="p-2 text-[#939BA0] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer" data-testid={`delete-client-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-[#687175]">
                    Aucun client trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllClientsPage;
